"use client";

import { ComponentPropsWithoutRef, FC } from "react";
import {
  getPricingLimitSubtitle,
  usePricingLimitVariant,
} from "../hooks/usePricingLimitVariant";
import PricingCards from "./PricingCards";
import Typography from "./Typography";

type NeedPaidPlanProps = {
  variant?: keyof typeof planToHeadings;
} & ComponentPropsWithoutRef<"section">;

const planToHeadings = {
  default: {
    title: "Unlock Premium features",
    description: "Choose a plan to continue and unlock Premium features!",
  },
  export_clip: {
    title: "Upgrade to unlock export",
    description: "Choose a plan to continue and export your clips!",
  },
  download_clip: {
    title: "Upgrade to download",
    description: "Choose a plan to continue and download your clips!",
  },
  remove_watermark: {
    title: "Upgrade to remove watermark",
    description:
      "Choose a plan to continue and export your clips without watermark!",
  },
  video_limit_reached: {
    title: "Upgrade to unlock more input videos",
    description:
      "Choose a plan to continue and process more input videos with your account!",
  },
  clip_limit_reached: {
    title: "Upgrade to unlock more clips",
    description:
      "Choose a plan to continue and generate more clips with your account!",
  },
  leftover_clips: {
    title: "Not enough clips left for a new video",
    description:
      "Each new video needs at least 10 clips. Choose a plan to unlock a larger clip allowance.",
  },
  export_limit_reached: {
    title: "Upgrade to unlock more exports",
    description: "Choose a plan to continue and unlock more exports!",
  },
  credit_limit_reached: {
    title: "Upgrade to unlock more upload minutes",
    description:
      "Choose a plan to continue and upload more video with your account!",
  },
  upload_limit_reached: {
    title: "Upgrade to unlock more uploads",
    description:
      "You've reached your plan's upload fair-use limit. Choose a plan to continue uploading!",
  },
  update_plan: {
    title: "Change your plan",
    description:
      "Compare our plans and find the one that best fits your needs.",
  },
  preview_lock: {
    title: "Upgrade to unlock",
    description:
      "Choose a plan to unlock and preview all of your generated clips!",
  },
  // The Auto post switch (useAutoPublishSwitch), once an account is connected.
  auto_post: {
    title: "Upgrade to unlock Auto post",
    description:
      "Choose a plan to schedule your shorts automatically.",
  },
  // cancelled-user-gating (helpers/cancelledAccess.ts): a lapsed subscriber.
  subscription_ended: {
    title: "Your subscription has ended",
    description:
      "Your clips are still here. Resubscribe to watch, edit, download and post them, and to create new ones.",
  },
};

const NeedPaidPlan: FC<NeedPaidPlanProps> = ({
  variant = "default",
  ...rest
}) => {
  const pricingLimitVariant = usePricingLimitVariant();
  // The default variant has no trigger-specific context, so reuse the
  // /pricing value-prop subtitle matching the user's pricing-limit-style
  // variant (the PricingCards below already adapt to it). Contextual
  // variants keep their "why you hit this paywall" copy.
  const description =
    variant === "default"
      ? getPricingLimitSubtitle(pricingLimitVariant)
      : planToHeadings[variant].description;

  return (
    <section {...rest} className={`${rest.className} `}>
      <div className="flex-col flex justify-center content-center items-center text-center my-4">
        <Typography variant="h4">{planToHeadings[variant].title}</Typography>
        <Typography variant="body1" className="mt-4 text-gray-600">
          {description}
        </Typography>
        <PricingCards className="mt-4 w-full" />
      </div>
    </section>
  );
};

export default NeedPaidPlan;
